import Trash from 'icons/lib/trash.svg';
import Link from 'next/link';
import { Button } from 'ui';
import { formatPrice } from 'utils';

import { useTranslation } from '@/i18n/client';
import { deleteItemFromCartEvent } from '@/modules/google/data-layer/data-layer';
import { Image } from '@/shared/components/image';
import { useBasket } from '@/shared/context/basket-context';
import { routes } from '@/shared/data/routes';
import { useCurrentLocale } from '@/shared/utils/client';
import { productName } from '@/shared/utils/product-name';

import styles from './CartProduct.module.scss';
import { CartProductProps } from './types';
import { imageBreakpoints } from './utils';

export const CartProduct = ({ product }: CartProductProps) => {
  const locale = useCurrentLocale();
  const { t } = useTranslation(['common']);
  const { setIsCartSidebarOpen, removeFromBasket } = useBasket();

  const quantityUnitAttribute = product.product?.attributes.find(
    (attribute) => attribute.unit
  );
  const quantityUnit = quantityUnitAttribute?.unit;

  const onDelete = async () => {
    const result = await removeFromBasket(product.productId);

    if (!result) {
      return;
    }

    deleteItemFromCartEvent({
      id: product.productId,
      amount: product.quantity,
      name: productName(product.product),
      price: product.price.amount,
      brand: product.brandName,
      categoryPath: product.categoryPath,
      currency: product.price.currency,
      importFlag: product.product?.importFlag,
    });
  };

  return (
    <div
      className={styles.cartProduct}
      id={`headerCart__product--${product.productId}`}
    >
      <Link
        href={`${routes[locale].SINGLE_PRODUCT}/${product.productSlug}`}
        prefetch={false}
        onClick={() => setIsCartSidebarOpen(false)}
      >
        <Image
          alt={productName(product.product)}
          title={productName(product.product)}
          breakpoints={imageBreakpoints}
          loader={false}
          className={styles['cartProduct--image']}
          src={product.photo}
        />
      </Link>

      <div className={styles.cartProduct__labels}>
        <div className={styles.cartProduct__labels__quantity}>
          <Link
            href={`${routes[locale].SINGLE_PRODUCT}/${product.productSlug}`}
            prefetch={false}
            className={styles.cartProduct__labels__priceWrapper}
            onClick={() => setIsCartSidebarOpen(false)}
          >
            {product.quantity > 1 ? (
              <>
                {formatPrice(
                  product.price.amount * product.quantity,
                  locale,
                  product.price.currency
                )}{' '}
                <span
                  className={styles['cartProduct__labels__priceWrapper--price']}
                >
                  ({product.quantity} x{' '}
                  {formatPrice(
                    product.price.amount,
                    locale,
                    product.price.currency
                  )}
                  )
                </span>
              </>
            ) : (
              formatPrice(product.price.amount, locale, product.price.currency)
            )}
          </Link>
          <Button
            buttonName="RemoveButton"
            size="icon"
            variant="iconButton"
            onPress={onDelete}
          >
            <Trash />
          </Button>
        </div>
        <Link
          className={styles['cartProduct__labels--name']}
          href={`${routes[locale].SINGLE_PRODUCT}/${product.productSlug}`}
          prefetch={false}
          onClick={() => setIsCartSidebarOpen(false)}
        >
          {productName(product.product)}
        </Link>

        <Link
          className={styles['cartProduct__labels__quantity--label']}
          href={`${routes[locale].SINGLE_PRODUCT}/${product.productSlug}`}
          prefetch={false}
          onClick={() => setIsCartSidebarOpen(false)}
        >
          {t('common.amount')}: {product.quantity}{' '}
          {product.product?.packageType ?? t('common.quantityUnit')}
        </Link>
      </div>
    </div>
  );
};
